import { CheckoutStep, StepProps } from "@/lib/types/checkout-steps";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { getBankIdMockScenario } from "@/lib/bankid-mock";
import { Spinner } from "../ui/spinner";
import { cn } from "@/lib/utils";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { capitalizeEachWord } from "@/lib/utils/strings";
import {
  setBankIdEmail,
  setBankIdEmailError,
  setBankIdPhone,
  setBankIdPhoneError,
} from "@/store/slices/bankid.slice";
import bankidLogo from "@/assets/bankid-logo.svg";
import fingerprintIcon from "@/assets/fingerprint-icon.svg";
import checkmark from "@/assets/check-mark.svg";
import {
  verifyBankIdSession,
  initiateBankId,
} from "@/store/thunks/bankid.thunks";
import { updateOrder, fetchOrder } from "@/store/thunks/order.thunks";
import { useTranslation } from "react-i18next";
import { isErrorCode } from "@/lib/error-messages";

const validatePhoneNumber = (number: string) => {
  const phoneRegex = /^07[0-9]{8}$/;
  return phoneRegex.test(number);
};

const validateEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export function BankIdStep({
  isActive,
  isCompleted,
  onComplete,
}: StepProps<CheckoutStep.BANK_ID>) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  const order = useAppSelector((state) => state.order.fetchedOrder.data);
  const {
    initiateBankIdLoading,
    verifyBankIdSessionLoading,
    error,
    verificationSuccess,
    verificationDetails,
    email,
    emailError,
    phone,
    phoneError,
  } = useAppSelector((state) => state.bankId);
  const location = useLocation();
  const { updateOrder: updateOrderState } = useAppSelector(
    (state) => state.order
  );

  useEffect(() => {
    const grandidSession = searchParams.get("grandidsession");
    if (grandidSession && !isCompleted && !verificationSuccess) {
      dispatch(verifyBankIdSession(grandidSession))
        .unwrap()
        .catch((error) => {
          console.error("BankID session verification failed:", error);
        });
    }
  }, [searchParams, isCompleted, dispatch, verificationSuccess]);

  const handleBankIdSign = async () => {
    if (!order?.id) return;

    try {
      const callback = `${window.location.origin}${location.pathname}`;
      const result = await dispatch(
        initiateBankId({
          orderId: order.id,
          callbackUrl: callback,
          thisDevice: false,
          mobileBankId: true,
          deviceChoice: false,
        })
      ).unwrap();

      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
      }
    } catch (error) {
      console.error("Failed to initiate BankID:", error);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    dispatch(setBankIdEmail(value));

    if (value && !validateEmail(value)) {
      dispatch(setBankIdEmailError(t("bankId.emailRequired")));
    } else {
      dispatch(setBankIdEmailError(null));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    dispatch(setBankIdPhone(value));

    if (value && !validatePhoneNumber(value)) {
      dispatch(setBankIdPhoneError(t("bankId.phoneRequired")));
    } else {
      dispatch(setBankIdPhoneError(null));
    }
  };

  const handleSubmit = async () => {
    if (
      !verificationSuccess ||
      !email ||
      !phone ||
      phoneError ||
      emailError ||
      !order?.id
    )
      return;

    const userData = verificationDetails?.userAttributes;
    if (!userData) return;

    try {
      await dispatch(
        updateOrder({
          orderId: order.id,
          attributes: {
            customer_email: email,
            metadata: {
              "Personal Number": userData.personalNumber,
              "Phone Number": phone,
            },
          },
        })
      ).unwrap();

      await dispatch(fetchOrder(order.id));

      onComplete({
        name: verificationSuccess.name,
        personalNumber: userData.personalNumber,
        email,
        phone,
      });
    } catch (error) {
      console.error("Failed to update order:", error);
    }
  };

  if (!isActive) return null;

  const isLoading =
    initiateBankIdLoading === "pending" ||
    initiateBankIdLoading === "fulfilled" ||
    verifyBankIdSessionLoading === "pending";

  const errorMessage =
    error && isErrorCode(error) ? t(`errors.${error}`) : error;

  const mockScenario = getBankIdMockScenario();

  return (
    <div>
      {mockScenario && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm overflow-hidden">
          <p className="font-medium text-amber-800">Dev: BankID mock active</p>
          <p className="text-amber-700 mt-1 break-words">
            Scenario: <code>{mockScenario}</code>. Change URL:{" "}
            <code className="break-all">
              ?bankid_mock=success|init_failed|verify_failed|expired|cancelled|unauthorized
            </code>
          </p>
        </div>
      )}

      {error && (
        <div className="text-red-500 text-sm mb-4" role="alert">
          {errorMessage}
        </div>
      )}

      {verificationSuccess ? (
        <div className="space-y-8">
          <div className="p-4 bg-success/20 rounded-md flex justify-between">
            <div className="w-[60%]">
              <p className="font-bold">
                {capitalizeEachWord(verificationSuccess.name)}
              </p>
              <p>{verificationSuccess.personalNumber}</p>
            </div>
            <div className="w-[40%] flex items-center justify-end">
              <img className="mr-1" src={checkmark} alt="BankID Checkmark" />
              <p className="text-darkforest font-medium">{t("bankId.verified")}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-[6px]">
              <Label htmlFor="email">{t("bankId.emailLabel")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder={t("bankId.emailPlaceholder")}
              />
              {emailError && (
                <p className="text-red-500 text-sm mt-1">{emailError}</p>
              )}
            </div>

            <div className="space-y-[6px]">
              <Label htmlFor="phone">{t("bankId.phoneLabel")}</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="07XXXXXXXX"
              />
              {phoneError && (
                <p className="text-red-500 text-sm mt-1">{phoneError}</p>
              )}
            </div>
          </div>

          <Button
            size="lg"
            variant="secondary"
            className="w-full"
            disabled={!email || !phone || !!phoneError || !!emailError}
            onClick={handleSubmit}
          >
            {updateOrderState.loading === "pending" ? (
              <Spinner size="sm" />
            ) : (
              t("bankId.continueToDelivery")
            )}
          </Button>
        </div>
      ) : (
        <>
          <p className="text-sm text-foreground leading-relaxed">
            {t("bankId.intro")}
          </p>

          <Button
            type="button"
            className={cn(
              "mt-5 w-full h-14 bg-bankid hover:bg-bankid/90 text-white font-medium text-base gap-3 px-6 shadow-none",
              isLoading && "opacity-70 cursor-not-allowed"
            )}
            onClick={handleBankIdSign}
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner size="sm" className="text-white" />
            ) : (
              <img
                src={fingerprintIcon}
                alt=""
                aria-hidden
                className="h-7 w-7 shrink-0"
              />
            )}
            <span>{t("bankId.start")}</span>
          </Button>

          <div className="mt-6 flex items-center justify-center gap-2.5">
            <img
              src={bankidLogo}
              alt="BankID"
              className="h-11 w-11 shrink-0"
            />
            <div className="space-y-0.5 text-sm text-foreground leading-snug">
              <p>{t("bankId.trustLine1")}</p>
              <p>{t("bankId.trustLine2")}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
